/**
 * `client-about` (crossmarc/steward-app#1768, docs/35-advisor-client-onboarding.md R3 "the
 * Fun List is a firm record"): the firm's team notes about a client's family — anniversary,
 * dreams, favorite media, hobbies, sports played, favorite teams, milestones, dislikes, food
 * and drink, allergies, alma mater. One row per `advisor-client` record
 * ({@link ADVISOR_CLIENT_TYPE_CODE}, `advisorClient.ts`), in the firm's own workspace, that the
 * firm keeps adding to as it learns about the family.
 *
 * ## Placement and shape, mirroring `advisor-client` / `onboarding-invitation`
 *
 * Same pack (`STEWARD_ADVISOR_FIRM_TYPE_DEFS`), same write shape: the façade creates and
 * updates it under the firm caller's own bearer token
 * (`services/steward-app-api/src/handlers/clientAbout.ts`), never a background role, and
 * `accessControl.writeCapability` is `STEWARD_WRITE_CAPABILITY` — the SAME union those two
 * types document: no role in THIS pack carries `steward:write`, but a firm workspace mounts
 * `steward-app` too, and `steward-admin`/`steward-member` on that side of the same membership
 * do (`packRoles.ts`).
 *
 * ## One row per client record, found by attribute, not by a shared id
 *
 * appcore's `createEntity` mints its own instanceId and takes no caller-supplied id, so this
 * type cannot be keyed BY `clientId` the way a direct `getEntity` lookup would want — the same
 * constraint `draftDismissal.ts` documents for its own type. The façade lists the firm's
 * `client-about` rows and matches by the `clientId` attribute
 * (`services/steward-app-api/src/handlers/clientAbout.ts`'s `findAboutRow`). Unlike
 * `draft-dismissal`, a second row for the same client is NOT harmless here — the whole point
 * is one mutable record the firm edits in place — so the write path updates the existing row
 * with `expectedVersion` (retrying once on a 409, exactly like `onboardingInvitation.ts`'s
 * `replaceInvitation`) rather than minting a new one when a row is already there. Two
 * concurrent FIRST writes for the same client can still each see "no row" and each create one;
 * that race is not closed here (see the handler's own header for the accepted, narrow cost).
 *
 * ## Each field carries its own source, and the source is server-stamped
 *
 * A value is never bare text. It is `{ value, source }`, where `source` is
 * `{ kind: 'firm' | 'client', userId, name, at }` — `kind: 'firm'` for a firm team member
 * (shown as "Added by Dana Lee, Oct 2"), `kind: 'client'` for the client's own onboarding
 * answer (shown as "From Sam"). **The façade stamps `source` from the caller's own identity
 * on every write; nothing in the request body can set it.** `kind: 'client'` values are
 * accepted by this schema so a later write path (the client-facing "About you" section,
 * crossmarc/steward-app#1765) can populate them, but nothing writes one yet — this PR only
 * ever stamps `kind: 'firm'`.
 *
 * ## Unchanged values keep their original source
 *
 * The write route is a merge over the fields a request actually names (see the handler): a
 * field whose submitted value equals what is already stored keeps its EXISTING source rather
 * than being re-stamped to the caller who happened to resend it. This is what lets the firm's
 * "About" panel be edited as a whole page (matching the mock's single Edit affordance) without
 * a client-supplied "Dreams" entry silently becoming "Added by" the next advisor who saves an
 * unrelated field.
 *
 * ## A closed field list — no identity fields, ever
 *
 * `CLIENT_ABOUT_FIELD_KEYS` is exhaustive and the schema is `strictObject`: a key outside the
 * eleven listed (in particular anything that looks like `ssn`, `dob`, `dateOfBirth`, an
 * account number, or any other identity field) is a validation failure, not a silently
 * accepted extra. Identity data has its own protected record (crossmarc/appcore#736) and must
 * never reach this type — docs/35's "no identity fields here" and issue #1768's own "no
 * identity fields" both name this explicitly.
 *
 * ## What this type is NOT
 *
 *   - **Not copied into a Steward workspace.** Setting up Steward for a client
 *     (crossmarc/steward-app#1767) copies the SSN and prefill data; it must not copy this row.
 *     docs/35: "About notes and firm documents are the firm's and are not copied."
 *   - **Not readable by the family.** A client's workspace membership (if one is ever set up)
 *     carries no membership in the FIRM's workspace, so there is no read path to this row from
 *     the family side at all — the same isolation `advisor-client` itself relies on.
 *   - **Not a vault of identity data.** See "A closed field list" above.
 *
 * ## Residual gap: a `steward:write` holder could forge `source`, IF this type were reachable
 * directly — it is not, on any deployed instance (independent review, 2026-09-25)
 *
 * A firm member who holds `steward:write` (every real `steward-admin`/`steward-member`) has the
 * capability this type's `accessControl.writeCapability` checks, so if they could reach
 * appcore's generic entity routes directly they could write this type's row with any `source`
 * they like, forging "From [client]" on their own text. The façade's stamping (below) is what an
 * ORDINARY write goes through; it is not, by itself, a barrier against a caller who skips the
 * façade. But skipping the façade is not actually possible from a firm member's own browser
 * session today: all three committed profiles (`deploy/instances/{dev,stage,prod}.json`, in the
 * appcore repo) declare `"memberPlane": "relay-only"`, which puts appcore's generic
 * `/w/{workspaceId}/entities/{typeCode}...` route behind the IAM (SigV4) authorizer rather than
 * the Cognito JWT one a browser's bearer token satisfies — see the handler's header for the exact
 * mechanism and file:line. This residual needs one of two things to actually become reachable: a
 * FUTURE façade route that forwards a caller-chosen type code on the entity/file CRUD surface
 * `appcoreTypeCodeIsClosed.test.ts` covers (none does today — pinned structurally, across the
 * whole service, not just this type; `links.ts`'s caller-supplied link-EDGE `toType` is a
 * narrower, separately documented exception — see that test's own registry note and
 * `inFlightPillarSurface.test.ts`'s "What this guard does NOT cover" — that lets a caller name
 * the type LABEL on a link edge, not choose which type's row a route reads or writes, so it is
 * not the same shape of gap), or an instance profile flipped back to `memberPlane`
 * absent/`"direct"`. Same class of gap as crossmarc/steward-app#1781, and not fixed here — see
 * the handler's header for the same note in the route's own words.
 */
import { z } from 'zod';

export const CLIENT_ABOUT_TYPE_CODE = 'client-about';

const id = z.string().min(1).max(200);
const instant = z.iso.datetime();

/** The cap on one field's free text. Generous enough for "A cabin near Estes Park; Priya
 *  wants to walk the Camino" and similar, short of inviting a firm to paste a whole bio. */
export const CLIENT_ABOUT_VALUE_MAX = 1000;

/**
 * The eleven fields issue #1768 names. Order and labels follow the signed-off mock
 * (`project/AboutTab.dc.html`, board "Firm: About tab") EXACTLY where the mock shows a row
 * for it — `anniversary`, `dreams`, `hobbies`, `favoriteTeams`, `foodAndDrink`, `allergies`,
 * `almaMater`, `milestones`, `dislikes`, in that order, are the mock's own nine rows. The
 * mock does not render `favoriteMedia` or `sportsPlayed` at all (they are in the issue's
 * field list but absent from the board itself); this list places them beside their nearest
 * narrative companions — media after dreams, sports played right after hobbies — which is a
 * judgment call this file owns, not something the mock decided. Exhaustive and closed — see
 * this file's header, "A closed field list". Adding a twelfth field means adding it here AND
 * to the mock; removing one is a breaking change to every stored row that carries it.
 */
export const CLIENT_ABOUT_FIELD_KEYS = [
  'anniversary',
  'dreams',
  'favoriteMedia',
  'hobbies',
  'sportsPlayed',
  'favoriteTeams',
  'foodAndDrink',
  'allergies',
  'almaMater',
  'milestones',
  'dislikes',
] as const;

export type ClientAboutFieldKey = (typeof CLIENT_ABOUT_FIELD_KEYS)[number];

/** Display labels. `foodAndDrink` is labelled with the mock's own wording ("Food and drink")
 *  rather than the issue text's "food/snacks/alcohol" — the mock is what RIPL signed off, so
 *  it wins where the two differ; the field still stores snacks and alcohol, only worded more
 *  plainly. The rest match the mock verbatim where it has a row, and the issue's own wording
 *  for the two fields the mock omits (`favoriteMedia`, `sportsPlayed`). */
export const CLIENT_ABOUT_FIELD_LABELS: Readonly<Record<ClientAboutFieldKey, string>> = {
  anniversary: 'Anniversary',
  dreams: 'Dreams',
  favoriteMedia: 'Favorite media',
  hobbies: 'Hobbies',
  sportsPlayed: 'Sports played',
  favoriteTeams: 'Favorite teams',
  foodAndDrink: 'Food and drink',
  allergies: 'Allergies',
  almaMater: 'Alma mater',
  milestones: 'Milestones',
  dislikes: 'Dislikes',
};

/** Who supplied a field's value. `client` is accepted here but nothing writes it until
 *  crossmarc/steward-app#1765 (the header's "Each field carries its own source"). */
export const CLIENT_ABOUT_SOURCE_KINDS = ['firm', 'client'] as const;
export type ClientAboutSourceKind = (typeof CLIENT_ABOUT_SOURCE_KINDS)[number];

export const AboutSourceSchema = z.strictObject({
  kind: z.enum(CLIENT_ABOUT_SOURCE_KINDS),
  userId: id,
  /** The display name shown beside the value ("Added by Dana Lee", "From Sam"), cleaned the
   *  same way `onboardingInvitation.ts`'s `clientName` is: `safeDisplayText`, whitespace
   *  collapsed, trimmed, at the façade before this is ever constructed. */
  name: z.string().min(1).max(200),
  at: instant,
});

export type AboutSource = z.infer<typeof AboutSourceSchema>;

export const AboutFieldSchema = z.strictObject({
  value: z.string().min(1).max(CLIENT_ABOUT_VALUE_MAX),
  source: AboutSourceSchema,
});

export type AboutField = z.infer<typeof AboutFieldSchema>;

/** Every field optional: a fresh record starts with none of the eleven present, and the
 *  firm fills them in over time (docs/35 R3, "keeps adding to"). */
export const ClientAboutFieldsSchema = z.strictObject(
  Object.fromEntries(
    CLIENT_ABOUT_FIELD_KEYS.map((key) => [key, AboutFieldSchema.optional()]),
  ) as Record<ClientAboutFieldKey, z.ZodOptional<typeof AboutFieldSchema>>,
);

export type ClientAboutFields = z.infer<typeof ClientAboutFieldsSchema>;

export const ClientAboutAttributesSchema = z.strictObject({
  firmId: id,
  /** The `advisor-client` row this About record belongs to (that type's own appcore
   *  `instanceId`). See this file's header, "One row per client record, found by attribute". */
  clientId: id,
  fields: ClientAboutFieldsSchema,
  updatedAt: instant,
  updatedBy: id,
});

export type ClientAboutAttributes = z.infer<typeof ClientAboutAttributesSchema>;

/** Reads a stored `client-about` row's attributes. `unreadable` covers both "does not parse"
 *  and "is missing", matching `readAdvisorClient`'s and `readDraftDismissal`'s convention. */
export function readClientAbout(
  value: unknown,
): { kind: 'about'; about: ClientAboutAttributes } | { kind: 'unreadable' } {
  const parsed = ClientAboutAttributesSchema.safeParse(value);
  return parsed.success ? { kind: 'about', about: parsed.data } : { kind: 'unreadable' };
}
